import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  CloseButton,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Tag,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { IoExit } from 'react-icons/io5';
import { MdCloudUpload, MdSave } from 'react-icons/md';

import Card from 'components/card/Card';
import { getApiErrorMessage } from 'utils/apiFieldErrors';
import {
  apiGetRFQPolicyInfo,
  apiSaveRFQPolicyInfo,
  type RFQPolicyInfoSaveResponse,
} from 'api/quote-marketplace';

const formatFileSize = (bytes: number): string =>
  `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Policy Information screen of a PL Specialty / CL Property quote (QM-109 to QM-113). */
const RFQQuoteView = () => {
  const { contactRef, quoteId } = useParams<{
    contactRef: string;
    quoteId: string;
  }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const activeStepColor = useColorModeValue('brand.500', 'brand.400');
  const mutedColor = useColorModeValue('gray.600', 'gray.300');
  const dropzoneBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  const id = Number(quoteId);
  const queryKey = ['quote-marketplace', 'rfq-policy-info', id];
  const {
    data: policyInfo,
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey,
    queryFn: () => apiGetRFQPolicyInfo(id),
  });

  const [quoteDescription, setQuoteDescription] = useState('');
  const [clientRequirements, setClientRequirements] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Seed the form once per quote; later saves keep what the broker typed.
  const loadedQuoteId = policyInfo?.quote_id;
  useEffect(() => {
    if (!policyInfo) return;
    setQuoteDescription(policyInfo.quote_description);
    setClientRequirements(policyInfo.client_requirements);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedQuoteId]);

  const onDrop = useCallback((files: File[]) => {
    setPendingFiles((prev) => [...prev, ...files]);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    disabled: isSaving,
  });

  const hasChanges =
    !!policyInfo &&
    (quoteDescription !== policyInfo.quote_description ||
      clientRequirements !== policyInfo.client_requirements ||
      pendingFiles.length > 0);

  const backToCustomer = () =>
    navigate(`/admin/quote-marketplace/customer/${contactRef}`);

  const handleSave = async (continueInR8: boolean) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await apiSaveRFQPolicyInfo(id, {
        quoteDescription,
        clientRequirements,
        files: pendingFiles,
      });
      queryClient.setQueryData(queryKey, response);
      setPendingFiles([]);
      toast({
        title: continueInR8
          ? `Saved to RFQ Ref # ${response.rfq_ref}. Continue the quote in Acturis R8.`
          : 'Saved',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      if (continueInR8) backToCustomer();
    } catch (err) {
      const body = err as Partial<RFQPolicyInfoSaveResponse>;
      if (body.attachments) {
        // Part of the save reached Acturis before the failure.
        queryClient.setQueryData(queryKey, body);
        setPendingFiles((prev) => prev.slice(body.files_filed ?? 0));
      }
      setSaveError(
        body.error ||
          getApiErrorMessage(
            err as Record<string, string | string[]>,
            'Failed to save to Acturis',
          ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Center py={20}>
        <Spinner size="xl" color="brand.500" />
      </Center>
    );
  }

  if (loadError || !policyInfo) {
    return (
      <Alert status="error" borderRadius="md" mt={4}>
        <AlertIcon />
        Quote could not be loaded.
      </Alert>
    );
  }

  return (
    <>
      <Flex justify="space-between" align="start" pt={4}>
        <Tabs index={0} variant="enclosed">
          <TabList gap={1} border={0}>
            <Tab
              cursor="default"
              _focus={{ boxShadow: 'none' }}
              borderBottomColor="white"
              bg="white"
            >
              <Text fontWeight="600" fontSize="sm">
                {`${policyInfo.product_target?.label ?? 'RFQ'} Quote 1`}
              </Text>
            </Tab>
          </TabList>
        </Tabs>

        <HStack spacing={2}>
          <Button
            leftIcon={<MdSave />}
            size="sm"
            variant="outline"
            onClick={() => handleSave(false)}
            isLoading={isSaving}
            isDisabled={!hasChanges}
          >
            Save
          </Button>
          <Button
            leftIcon={<IoExit />}
            size="sm"
            variant="outline"
            onClick={backToCustomer}
            isDisabled={isSaving}
          >
            Exit
          </Button>
        </HStack>
      </Flex>
      <Card p={0} overflow="hidden" roundedTop={0}>
        <Flex
          mt="8"
          mx={4}
          px={4}
          bg="gray.200"
          rounded="lg"
          justify="space-between"
          align="center"
        >
          <Text
            fontSize="sm"
            fontWeight="600"
            py={3}
            color={activeStepColor}
            borderBottom="2px solid"
            borderBottomColor="brand.500"
          >
            Policy Information
          </Text>
          <Text fontSize="sm" fontWeight="600" color={mutedColor}>
            RFQ Ref # {policyInfo.rfq_ref}
          </Text>
        </Flex>

        <Box p={4} minH="400px">
          <VStack spacing={5} align="stretch">
            {saveError && (
              <Alert status="error" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {saveError}
              </Alert>
            )}

            <FormControl>
              <FormLabel>Quote Description</FormLabel>
              <Input
                value={quoteDescription}
                onChange={(e) => setQuoteDescription(e.target.value)}
                isDisabled={isSaving}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Client Requirements</FormLabel>
              <Textarea
                rows={5}
                value={clientRequirements}
                onChange={(e) => setClientRequirements(e.target.value)}
                isDisabled={isSaving}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Attachments</FormLabel>
              <Flex
                direction="column"
                align="center"
                justify="center"
                gap={2}
                p={6}
                border="1px dashed"
                borderColor={isDragActive ? 'brand.500' : borderColor}
                borderRadius="md"
                bg={dropzoneBg}
                cursor={isSaving ? 'not-allowed' : 'pointer'}
                {...getRootProps()}
              >
                <input {...getInputProps()} />
                <Icon as={MdCloudUpload} boxSize={8} color={activeStepColor} />
                <Text fontSize="sm" color={mutedColor}>
                  Drag and drop files here, or browse
                </Text>
              </Flex>

              <VStack align="stretch" spacing={2} mt={3}>
                {policyInfo.attachments.map((attachment) => (
                  <Flex
                    key={attachment.document_ref}
                    justify="space-between"
                    align="center"
                    px={3}
                    py={2}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                  >
                    <Text fontSize="sm">
                      {attachment.file_name}{' '}
                      <Text as="span" color={mutedColor}>
                        ({formatFileSize(attachment.file_size)})
                      </Text>
                    </Text>
                    <Tag size="sm" colorScheme="green">
                      Uploaded
                    </Tag>
                  </Flex>
                ))}
                {pendingFiles.map((file, index) => (
                  <Flex
                    key={`${file.name}-${index}`}
                    justify="space-between"
                    align="center"
                    px={3}
                    py={2}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                  >
                    <Text fontSize="sm">
                      {file.name}{' '}
                      <Text as="span" color={mutedColor}>
                        ({formatFileSize(file.size)})
                      </Text>
                    </Text>
                    {isSaving ? (
                      <HStack spacing={2}>
                        <Spinner size="xs" />
                        <Text fontSize="xs" color={mutedColor}>
                          Uploading
                        </Text>
                      </HStack>
                    ) : (
                      <CloseButton
                        size="sm"
                        aria-label={`Remove ${file.name}`}
                        onClick={() =>
                          setPendingFiles((prev) =>
                            prev.filter((_, i) => i !== index),
                          )
                        }
                      />
                    )}
                  </Flex>
                ))}
              </VStack>
            </FormControl>
          </VStack>
        </Box>

        <Flex
          justify="flex-end"
          gap={3}
          p={4}
          borderTop="1px solid"
          borderColor={borderColor}
        >
          <Button
            variant="brand"
            onClick={() => handleSave(true)}
            isLoading={isSaving}
          >
            Save & Continue in R8
          </Button>
        </Flex>
      </Card>
    </>
  );
};

export default RFQQuoteView;
